"use client";

import React from "react";
import { MapPin, ArrowRight, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const serviceAreas = [{
  name: "Port Charlotte",
  path: "/PortCharlottePestControlPage",
  isMain: true,
  description: "Our primary service hub, providing rapid response and comprehensive pest management for homes and businesses throughout the heart of Charlotte County.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/port_charlotte_aerial_view.png"
}, {
  name: "Punta Gorda",
  path: "/PuntaGordaPestControlPage",
  isMain: false,
  description: "Expert pest control protecting historic homes and waterfront properties.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/punta_gorda_waterfront.png"
}, {
  name: "Cape Coral",
  path: "/CapeCoralPestControlPage",
  isMain: false,
  description: "Specialized treatments for canal-front homes and growing neighborhoods.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/cape_coral_canals.png"
}, {
  name: "North Fort Myers",
  path: "/NorthFortMyersPestControlPage",
  isMain: false,
  description: "Reliable extermination services tailored to local environmental needs.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/north_fort_myers_landscape.png"
}, {
  name: "Arcadia",
  path: "/ArcadiaPestControlPage",
  isMain: false,
  description: "Dedicated pest solutions for rural and residential properties.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_rural_florida.png"
}, {
  name: "Burnt Store",
  path: "/BurntStorePestControlPage",
  isMain: false,
  description: "Targeted pest management for marina and golf course communities.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/burnt_store_marina.png"
}];
export default function PortCharlotteServiceAreaNearbyCities() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="port-charlotte-service-area-nearby-cities" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Badge variant="secondary" className="mb-4">
            <MapPin className="w-4 h-4 mr-1" />
            Local Service Areas
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
            Proudly Serving Port Charlotte & Beyond
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            As your local experts, we provide top-tier <strong className="text-foreground font-semibold">Port Charlotte pest control</strong> serving surrounding areas including Punta Gorda, Cape Coral, and neighboring communities. We are committed to keeping Southwest Florida homes safe and pest-free.
          </p>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-1000 ease-out delay-200 ${isGridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {serviceAreas.map((area, index) => <div key={area.name} data-index={index} className={area.isMain ? "md:col-span-2 lg:col-span-3" : ""}>
              <Card className={`h-full flex flex-col overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border-border/50 ${area.isMain ? 'bg-primary/5 border-primary/20' : 'bg-card'}`}>
                {area.isMain && <div className="w-full h-48 md:h-64 overflow-hidden relative bg-muted">
                    <img src={area.image} alt={`${area.name} pest control service area map`} data-wvc-srcset={`${area.image}-wvc-srcset`} data-wvc-sizes={`${area.image}-wvc-sizes`} className="w-full h-full object-cover opacity-80 mix-blend-multiply" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
                    <Badge className="absolute top-4 left-4 shadow-sm" variant="default">
                      <ShieldCheck className="w-4 h-4 mr-1" />
                      Main Headquarters
                    </Badge>
                  </div>}
                
                <CardHeader className={area.isMain ? "pt-6" : ""}>
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className={`font-default ${area.isMain ? 'text-2xl md:text-3xl' : 'text-xl'}`}>
                      {area.name}
                    </CardTitle>
                    {!area.isMain && <MapPin className="w-5 h-5 text-primary/70" />}
                  </div>
                  <CardDescription className="text-base">
                    {area.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="flex-grow">
                  {}
                </CardContent>
                
                <CardFooter className="pt-4 border-t border-border/40 mt-auto">
                  <Button asChild variant={area.isMain ? "default" : "ghost"} className={`w-full justify-between group ${area.isMain ? '' : 'hover:bg-primary/10 hover:text-primary'}`}>
                    <Link to={area.path}>
                      <span>View {area.name} Services</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>

        {}
        <div className="mt-16 text-center">
          <p className="text-muted-foreground mb-6">
            Don't see your city listed? We likely still cover your area!
          </p>
          <Button asChild size="lg" variant="outline" className="rounded-lg px-8">
            <Link to="/ContactPage">
              Contact Us to Verify Coverage
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}